import * as React from 'react';
import { Controller, type Control, type FieldValues } from 'react-hook-form';

import { OtpVerification, type OtpVerificationInputProps } from '../composites/OtpVerification';
import { FormField } from '../patterns/FormField';

import { rhfInlineError } from './rhfInlineError';
import type { RhfLayoutProps } from './rhfLayout';

export interface RhfOtpInputProps extends Omit<OtpVerificationInputProps, 'value' | 'onChange' | 'error'>, RhfLayoutProps {
  control: Control<FieldValues>;
  name: string;
  label: string;
  hint?: string;
  required?: boolean;
  semanticId?: string;
  disabled?: boolean;
}

/**
 * OTP input adapter for React Hook Form.
 *
 * Wraps `OtpVerification.Input` inside a FormField for label/error/hint display.
 * Stores the OTP value as a string in the form state.
 */
export const RhfOtpInput = React.forwardRef<HTMLDivElement, RhfOtpInputProps>(
  function RhfOtpInput(
    {
      control,
      name,
      label,
      hint,
      required = false,
      semanticId,
      disabled = false,
      numInputs = 6,
      success,
      id,
      controlWidth,
    },
    ref,
  ) {
    return (
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState, formState }) => {
          const errorText = rhfInlineError(fieldState, { isSubmitted: formState.isSubmitted });

          return (
            <FormField
              ref={ref}
              label={label}
              hint={hint}
              required={required}
              semanticId={semanticId}
              disabled={disabled}
              error={errorText}
              controlWidth={controlWidth}
            >
              <OtpVerification.Input
                value={field.value ?? ''}
                onChange={(otp) => field.onChange(otp)}
                numInputs={numInputs}
                error={Boolean(errorText)}
                success={success}
                disabled={disabled}
                id={id ?? name}
              />
            </FormField>
          );
        }}
      />
    );
  },
);

RhfOtpInput.displayName = 'RhfOtpInput';
